import { Grid, Stack } from "@mui/material";
import {
  ApiAttributionModel,
  ApiChartCrawler,
  ApiChartTemplate,
  ApiChartType,
  ApiDimension,
  ApiMetric,
  ApiYAxisChartType,
} from "@incendium/api";
import StandardAnalyticsCard from "features/analytics/components/StandardAnalyticsCard";
import { AnalyticsChartStatCard, table10Rows } from "features/analytics";
import { AnalyticsSpacing } from "consts";
import { useState } from "react";
import { useMetricName } from "features/analytics/hooks/useMetricName";
import {
  metricsByAttributionModelType,
  excludeMediaSpendMetrics,
} from "features/analytics/services/attributionMapping";
import {
  CHART_KEY_ORGANIC_OVERVIEW_DEVICE_PERFORMANCE,
  CHART_KEY_ORGANIC_OVERVIEW_SESSIONS_VS_METRIC,
} from "features/analytics/constants/chartKeys";

function Overview({ organicChannelName }: { organicChannelName: string }) {
  const metricToName = useMetricName();
  const [metric, setMetric] = useState(
    ApiMetric.METRIC_MACRO_CONVERSIONS_COUNT,
  );

  return (
    <Grid container spacing={AnalyticsSpacing} mb={4}>
      <Grid item xs={3}>
        <Stack spacing={AnalyticsSpacing}>
          <AnalyticsChartStatCard
            hideTrendedSpark
            noToolbar
            forceComparison
            chart={{
              name: "No. Organic Visits",
              dimension: [],
              template: ApiChartTemplate.SNAPSHOT,
              type: ApiChartType.GRAPH,
              attributes: [
                {
                  key: ApiDimension.DIMENSION_CHANNEL,
                  value: organicChannelName,
                },
              ],
              yAxisKeys: [
                {
                  key: "l",
                  fields: [ApiMetric.METRIC_SESSIONS_COUNT],
                },
              ],
              displayOptions: {
                statTitle: "No. Organic Visits",
              },
            }}
          />

          <AnalyticsChartStatCard
            hideTrendedSpark
            noToolbar
            forceComparison
            chart={{
              name: "",
              dimension: [],
              template: ApiChartTemplate.SNAPSHOT,
              type: ApiChartType.GRAPH,
              attributes: [
                {
                  key: ApiDimension.DIMENSION_CHANNEL,
                  value: organicChannelName,
                },
              ],
              yAxisKeys: [
                {
                  key: "l",
                  fields: [metric],
                },
              ],
            }}
          />

          <AnalyticsChartStatCard
            hideTrendedSpark
            noToolbar
            forceComparison
            chart={{
              name: "bots",
              dimension: [],
              template: ApiChartTemplate.SNAPSHOT,
              type: ApiChartType.GRAPH,
              crawler: ApiChartCrawler.ONLY_CRAWLERS,
              attributes: [],
              yAxisKeys: [
                {
                  key: "l",
                  fields: [ApiMetric.METRIC_SESSIONS_COUNT],
                },
              ],
              displayOptions: {
                statTitle: "No. of visits by bots in this time period",
              },
            }}
          />
        </Stack>
      </Grid>
      <Grid item xs={9}>
        <StandardAnalyticsCard
          chartKey={CHART_KEY_ORGANIC_OVERVIEW_SESSIONS_VS_METRIC}
          onChangeMetric={setMetric}
          chart={{
            name: `Organic Sessions vs ${metricToName(metric)}`,
            dimension: [ApiDimension.DIMENSION_SESSION_START_BY_DAY],
            template: ApiChartTemplate.TREND,
            type: ApiChartType.GRAPH,
            attributes: [
              {
                key: ApiDimension.DIMENSION_CHANNEL,
                value: organicChannelName,
              },
            ],
            yAxisKeys: [
              {
                key: "l",
                fields: [metric],
              },
              {
                key: "r",
                fields: [ApiMetric.METRIC_SESSIONS_COUNT],
                chart: {
                  [ApiMetric.METRIC_SESSIONS_COUNT]: ApiYAxisChartType.BAR,
                },
              },
            ],
            displayOptions: {
              noFilters: true,
              metricsOrder: [ApiMetric.METRIC_SESSIONS_COUNT, metric],
              availableMetrics: excludeMediaSpendMetrics(
                metricsByAttributionModelType(
                  ApiAttributionModel.NO_MODEL_SELECTED,
                ) as ApiMetric[],
              ),
            },
          }}
        />
      </Grid>
      <Grid item xs={12} sx={{ minHeight: table10Rows, flexGrow: 1 }}>
        <StandardAnalyticsCard
          chartKey={CHART_KEY_ORGANIC_OVERVIEW_DEVICE_PERFORMANCE}
          chart={{
            name: "Device Performance",
            dimension: [
              ApiDimension.DIMENSION_SESSION_START_BY_DAY,
              ApiDimension.DIMENSION_DEVICE,
            ],
            template: ApiChartTemplate.TREND,
            type: ApiChartType.GRAPH,
            attributes: [
              {
                key: ApiDimension.DIMENSION_CHANNEL,
                value: organicChannelName,
              },
            ],
            yAxisKeys: [
              {
                key: "l",
                fields: [ApiMetric.METRIC_SESSIONS_COUNT],
                chart: {
                  [ApiMetric.METRIC_SESSIONS_COUNT]: ApiYAxisChartType.BAR,
                },
                stackId: "l",
              },
            ],
            displayOptions: {
              noFilters: true,
              availableMetrics: [
                ApiMetric.METRIC_SESSIONS_COUNT,
                ApiMetric.METRIC_EFFECTIVE_SESSION_RATE,
                ApiMetric.METRIC_SESSION_SALES_COUNT,
                ApiMetric.METRIC_REVENUE,
                ApiMetric.METRIC_MACRO_CONVERSION_RATE,
              ],
            },
          }}
        />
      </Grid>
    </Grid>
  );
}

export default Overview;
